'use client';

import { motion } from 'framer-motion';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Input } from './input';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.02,
    },
  },
};

const inputVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.12, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] },
  },
};

const VerificationInput = ({
  onChange,
  onComplete,
  shake = false,
}: {
  onChange?: (code: string) => void;
  onComplete?: (code: string) => void;
  shake?: boolean;
}) => {
  const [code, setCode] = useState<Array<string>>(Array(6).fill(''));
  const [inputsRef, setInputsRef] = useState<Array<React.RefObject<HTMLInputElement>>>([]);

  useEffect(() => {
    setInputsRef(
      Array(6)
        .fill(0)
        .map(() => React.createRef<HTMLInputElement>() as React.RefObject<HTMLInputElement>)
    );
  }, []);

  useEffect(() => {
    const areAllDigitsFilled = code.every((digit) => digit !== '');
    const codeAsString = code.join('');

    onChange?.(codeAsString);

    if (areAllDigitsFilled) {
      onComplete?.(codeAsString);
    }
  }, [code, onChange, onComplete]);

  const updateCode = (digit: string, i: number) => {
    setCode((prevCode) => [...prevCode.slice(0, i), digit, ...prevCode.slice(i + 1)]);
  };

  const focusInput = (i: number) => {
    inputsRef[i].current?.focus();
  };

  const handleChange = (digit: string, i: number) => {
    const firstEmptyIndex = code.findIndex((c) => c === '');
    const isDeletingMiddleInput = digit === '' && i > 0 && i < code.length - 1;
    const isLastInput = i === code.length - 1;
    const isTypingAndNextInputExists = digit !== '' && inputsRef[i + 1];

    if (firstEmptyIndex !== -1 && firstEmptyIndex < i) {
      focusInput(firstEmptyIndex);
      return;
    }

    if (/[^0-9]/.test(digit) && digit !== '') {
      return;
    }

    updateCode(digit, i);

    if (isDeletingMiddleInput && isLastInput) {
      focusInput(i - 1);
    } else if (isTypingAndNextInputExists) {
      focusInput(i + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Backspace' && code[i] === '' && i > 0) {
      updateCode('', i - 1);
      focusInput(i - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, i: number) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    // Extract only digits from pasted text
    const digits = pastedText.replace(/[^0-9]/g, '').slice(0, 6);

    if (digits.length === 0) {
      return;
    }

    // Update all fields with pasted digits starting from the current index
    setCode((prevCode) => {
      const newCode = [...prevCode];
      digits.split('').forEach((digit, idx) => {
        if (i + idx < newCode.length) {
          newCode[i + idx] = digit;
        }
      });
      return newCode;
    });

    // Focus on the next empty input or the last input if all filled
    const nextEmptyIndex = i + digits.length;
    const targetIndex = nextEmptyIndex < code.length ? nextEmptyIndex : code.length - 1;

    // Use setTimeout to ensure the state update has been applied
    setTimeout(() => {
      focusInput(targetIndex);
    }, 0);
  };

  const isComplete = code.every((digit) => digit !== '');

  return (
    <motion.div
      className='flex items-center justify-center gap-3'
      variants={containerVariants}
      initial='hidden'
      animate={shake ? { x: [-10, 10, -10, 10, 0] } : 'visible'}
      transition={shake ? { duration: 0.4 } : undefined}>
      {inputsRef.map((inputRef, i) => (
        <motion.div key={i} variants={inputVariants} className='aspect-square w-full max-w-14'>
          <Input
            wrapperClassName='w-full h-full'
            className={`h-14 w-14 border bg-white text-center text-2xl font-semibold transition-all duration-150 placeholder:text-zinc-300 focus:outline-none focus:ring-0 disabled:bg-zinc-50 disabled:text-zinc-300 ${
              code[i]
                ? isComplete
                  ? 'scale-[1.02] border-zinc-900 bg-zinc-50 text-zinc-900'
                  : 'scale-[1.02] border-zinc-400 text-zinc-900'
                : 'scale-100 border-zinc-200 text-zinc-900 hover:border-zinc-300 focus:border-zinc-900'
            }`}
            type='tel'
            maxLength={1}
            ref={inputRef}
            value={code[i]}
            placeholder=''
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value, i)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, i)}
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e, i)}
          />
        </motion.div>
      ))}
    </motion.div>
  );
};

export default VerificationInput;
